// ============================================================
// Typed EventBus with proper cleanup
// ============================================================
import type { PlayerEvents } from './types';

type Handler<T> = T extends void ? () => void : (data: T) => void;

export class EventBus {
  private listeners = new Map<string, Set<Function>>();

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof PlayerEvents>(event: K, handler: Handler<PlayerEvents[K]>): () => void {
    if (!this.listeners.has(event as string)) {
      this.listeners.set(event as string, new Set());
    }
    this.listeners.get(event as string)!.add(handler);

    return () => this.off(event, handler);
  }

  /** Subscribe to an event, auto-unsubscribe after first call. */
  once<K extends keyof PlayerEvents>(event: K, handler: Handler<PlayerEvents[K]>): void {
    const wrapper = ((...args: unknown[]) => {
      this.off(event, wrapper);
      (handler as Function)(...args);
    }) as Handler<PlayerEvents[K]>;
    this.on(event, wrapper);
  }

  /** Emit an event with payload. */
  emit<K extends keyof PlayerEvents>(
    event: K,
    ...args: PlayerEvents[K] extends void ? [] : [PlayerEvents[K]]
  ): void {
    const handlers = this.listeners.get(event as string);
    if (!handlers) return;
    for (const handler of handlers) {
      try {
        handler(...args);
      } catch (e) {
        console.error(`[EventBus] Error in handler for "${String(event)}":`, e);
      }
    }
  }

  /** Remove a specific handler for an event. */
  off<K extends keyof PlayerEvents>(event: K, handler: Function): void {
    this.listeners.get(event as string)?.delete(handler);
  }

  /** Remove all handlers for an event, or all events if no event specified. */
  removeAll(event?: keyof PlayerEvents): void {
    if (event) {
      this.listeners.delete(event as string);
    } else {
      this.listeners.clear();
    }
  }
}
